<template>
  <div
    class="w-50 compare-pane h-100 overflow-y-auto"
    :class="{
      'compare-pane-diff': isDiffPane,
      'compare-pane-left': rightBorder,
      'only-compare-locked': onlyCompareLocked,
    }"
    role="region"
    :aria-label="ariaLabel"
  >
    <div class="pane-inner pa-6">
      <div class="d-flex align-start justify-space-between ga-3 py-2">
        <div
          v-if="titleHtml != null"
          class="text-h5 font-weight-medium"
        >
          <DpplSafeHtml :html="titleHtml" />
        </div>
        <div
          v-else
          class="text-h5 font-weight-medium"
        >
          {{ profile?.title || '' }}
        </div>
        <a
          v-if="editorHref"
          :href="editorHref"
          class="text-body-2 text-primary"
          target="_blank"
          rel="noopener noreferrer"
        >
          View in Editor
          <IconOpen class="job-profile-open-icon" />
        </a>
      </div>
      <div class="meta-diff text-body-2 text-medium-emphasis mb-2 d-flex ga-4 align-center">
        <div>
          <JobProfileStatus
            :status="profile?.status"
            :is-hris-profile="profile?.is_hris_profile"
            :is-template="profile?.is_template || profile?.type_key === 'job_profile_template'"
          />
        </div>
        <DpplSafeHtml
          v-if="metaHtml != null"
          :html="metaHtml"
        />
        <template v-else>{{ subtitle }}</template>
      </div>
      <div class="d-flex align-center ga-2 flex-wrap mb-2">
        <span class="text-caption text-medium-emphasis">
          <template v-if="profile?.effective_date">
            Effective {{ formatDisplayDate(profile?.effective_date) }}
          </template>
          <template v-else>
            Created {{ formatDisplayDate(profile?.created_at) }}
          </template>
        </span>
        <span
          v-if="getQuarterYearLabel(profile?.effective_date)"
          class="revision-badge revision-badge-quarter text-caption"
        >
          {{ getQuarterYearLabel(profile?.effective_date) }}
        </span>
        <CurrentVersionBadge v-if="isCurrentVersion" />
        <span
          v-if="acknowledgedAt"
          class="text-caption text-medium-emphasis"
        >
          Employee Acknowledged on {{ formatDisplayDate(acknowledgedAt) }}
        </span>
      </div>

      <div
        v-for="field in fields"
        :key="field.type"
        class="py-2 field-row"
      >
        <div class="d-flex justify-end align-center ga-1 mt-2 mb-1">
          <v-btn
            v-if="!isEmptyHtml(field.headerHtml + field.html + field.footerHtml)"
            class="copy-button"
            variant="text"
            size="x-small"
            density="comfortable"
            icon
            :aria-label="`Copy ${field.label}`"
            @click="copyFieldText(field)"
          >
            <v-icon
              icon="$content-copy"
              size="14"
            />
          </v-btn>
          <FieldLabel>{{ field.label }}</FieldLabel>
        </div>
        <DocumentPreviewFieldHtml class="field-content">
          <div
            v-if="field.headerHtml && !isEmptyHtml(field.headerHtml)"
            class="locked-field"
          >
            <DpplSafeHtml :html="field.headerHtml" />
          </div>
          <DpplSafeHtml
            v-if="field.html"
            :html="field.html"
          />
          <div
            v-if="field.footerHtml && !isEmptyHtml(field.footerHtml)"
            class="locked-field"
          >
            <DpplSafeHtml :html="field.footerHtml" />
          </div>
        </DocumentPreviewFieldHtml>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import FieldLabel from '@/components/editor/field/FieldLabel.vue';
import DpplSafeHtml from '@ui/components/general/DpplSafeHtml/DpplSafeHtml.vue';
import DocumentPreviewFieldHtml from '@ui/components/general/DocumentPreviewFieldHtml/DocumentPreviewFieldHtml.vue';
import JobProfileStatus from '@/components/jobProfiles/editor/JobProfileStatus.vue';
import CurrentVersionBadge from '@/components/shared/CurrentVersionBadge.vue';
import { htmlToText, isEmptyHtml } from '@ui/helpers/htmlToText';
import { formatDisplayDate, getQuarterYearLabel } from '@/helpers/date';
import { writeToClipboardApi, writeToClipboardExecCommand } from '@/helpers/copyToClipboard';
import { useAppSnackbar } from '@/stores/app/snackbar';
import IconOpen from '@/assets/icon_open.svg?component';

const props = defineProps({
  profile: {
    type: Object,
    default: null,
  },
  fields: {
    type: Array,
    default: () => [],
  },
  subtitle: {
    type: String,
    default: '',
  },
  isCurrentVersion: {
    type: Boolean,
    default: false,
  },
  isDiffPane: {
    type: Boolean,
    default: false,
  },
  // Diff HTML for the title. Don't pass to use profile.title
  titleHtml: {
    type: String,
    default: undefined,
  },
  // Diff HTML for metadata (location, job_code, level). Don't pass to use plain subtitle
  metaHtml: {
    type: String,
    default: undefined,
  },
  acknowledgedAt: {
    type: String,
    default: null,
  },
  profileUlid: {
    type: String,
    default: null,
  },
  // Append ?sidebar=history so the editor opens with Version History in the right panel
  editorOpenVersionHistory: {
    type: Boolean,
    default: false,
  },
  ariaLabel: {
    type: String,
    required: true,
  },
  rightBorder: {
    type: Boolean,
    default: false,
  },
  onlyCompareLocked: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const { addSuccessSnackbar, addErrorSnackbar } = useAppSnackbar();

const copyFieldText = async (field) => {
  const text = [field.headerHtml, field.html, field.footerHtml]
    .filter((html) => html && !isEmptyHtml(html))
    .map((html) => htmlToText(html).trim())
    .join('\n\n')
    .trim();

  try {
    await writeToClipboardApi(text, false);
  } catch (err) {
    try {
      writeToClipboardExecCommand(text);
    } catch (fallbackErr) {
      addErrorSnackbar({ text: 'Failed to copy to clipboard' });
      return;
    }
  }
  addSuccessSnackbar({ text: `${field.label} copied to clipboard` });
};

const editorHref = computed(() => {
  if (!props.profileUlid) return null;

  const query = props.editorOpenVersionHistory ? { sidebar: 'history' } : {};

  return router.resolve({
    name: 'job-profile-hris-view',
    params: { id: props.profileUlid },
    query,
  }).href;
});
</script>

<style scoped lang="less">
.compare-pane {
  min-height: 0;
}

.compare-pane-left {
  border-right: 1px solid @stroke-default;
}

.compare-pane-diff {
  .locked-field, .meta-diff, &:not(.only-compare-locked) {
    :deep(.removed-diff) {
      background: @red-200;
      text-decoration: line-through;
    }
    :deep(.added-diff) {
      background: @green-200;
    }
  }
}

.job-profile-open-icon {
  width: 13px;
  height: 13px;
}

.field-row {
  .copy-button {
    opacity: 0;
    transition: opacity 0.15s ease-in-out;
  }

  &:hover .copy-button {
    opacity: 0.5;
  }
}

.field-content {
  :deep(h1), :deep(h2), :deep(h3) {
    line-height: 28px;
    font-weight: 700;
    padding: 8px 40px 8px 0px;
  }
  :deep(h1) {
    font-size: 24px;
  }
  :deep(h2) {
    font-size: 20px;
  }
  :deep(h3) {
    font-size: 18px;
  }
  :deep(p), :deep(section), :deep(article) {
    font-size: 16px;
    line-height: 28px;
    font-weight: 300;
    padding: 8px 40px 8px 0px;
  }
  :deep(span) {
    font-size: 16px;
    line-height: 28px;
  }
  :deep(ul), :deep(ol) {
    padding: 0;

    li {
      font-size: 16px;
      line-height: 28px;
      font-weight: 300;
      max-width: 524px;
    }
  }
}

.locked-field {
  background-color: fade(@bg-brand-default, 5%);
}

.revision-badge {
  padding: 2px 8px;
  background-color: @grey-400;
  border-radius: 24px;
}

.revision-badge-quarter {
  background-color: @orange-100;
  color: @orange-500;
}

</style>
